import { WarningOutlined } from '@ant-design/icons'
import { type ApolloError, getApolloContext } from '@apollo/client'
import { isForbiddenError } from '@salescore/client-base'
import { CardContainer, type HandledError } from '@salescore/client-common'
import { Link, useNavigate } from '@tanstack/react-router'
import { Button, message, Result, Tooltip } from 'antd'
import { useContext } from 'react'
import { useTranslation } from 'react-i18next'

export const useHandleApolloError = ({
  error,
  isSmallComponent,
}: {
  error: ApolloError
  isSmallComponent?: boolean
}): HandledError => {
  const { client } = useContext(getApolloContext())
  const navigate = useNavigate()
  const { t } = useTranslation()
  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
  if (isSmallComponent) {
    return {
      component: (
        <Tooltip
          styles={{ body: { width: 400 } }}
          title={
            <>
              {t(`エラーメッセージ : `)}
              {error.message} <br />
              {t(
                `エラーにより表示できませんでした。リロードしても解決しない場合は右下のチャットボットからお問い合わせください。`,
              )}
            </>
          }
        >
          <Button shape="round" icon={<WarningOutlined />}>
            <span className="text-xs font-bold">{t(`表示できません`)}</span>
          </Button>
        </Tooltip>
      ),
      effect(): void {
        //  empty
      },
    }
  }

  if (error.message.includes('Unauthorized')) {
    return {
      component: (
        <Result
          status="403"
          title=""
          subTitle={`${t(`不正な認証情報です。再度ログインし直してください。`)} ${error.message}`}
          extra={
            <div className="text-center">
              <Link to="/sign_out">
                <Button type="default">{t(`ログイン画面へ`)}</Button>
              </Link>
            </div>
          }
        />
      ),

      effect(): void {
        // 古いキャッシュを読み込み続けてエラーになるケースがあるため、キャッシュクリアする
        void client?.resetStore()

        if (error.message.startsWith(`この組織へのアクセス権限がありません`)) {
          void message.error(t(`権限がありません。トップページにリダイレクトします。`))
          void navigate({ to: '/' })
        }

        if (error.message.includes(`トークンの有効期限が切れています`)) {
          void message.error(t(`認証の有効期限が切れました。再ログインしてください`))
          void navigate({ to: '/sign_out' })
        }
        if (error.message.startsWith(`存在しないアカウントです`)) {
          // void message.error(`アカウントがありません。リダイレクトします。`) // 新規登録時もここを通りうるのでメッセージを表示しない
          // notifyBugsnag({ error: new UnauthorizedApolloError(error), user: myIdentity, severity: 'info' }) // /sign_upで登録できなければエラーを通知
          void navigate({ to: '/sign_up' })
        }
        if (error.message === 'Unauthorized: User not found') {
          void message.error(t(`SALESCOREの利用には管理者からの登録が必要です。`))
          void navigate({ to: '/error', search: { type: 'user_not_found' } })
        }
      },
    }
  }

  if (isForbiddenError(error)) {
    return {
      component: (
        <CardContainer withLogo={false} size="medium">
          <Result
            status="403"
            title={t(`この操作は許可されていません`)}
            subTitle={error.message}
            extra={
              <div className="text-center">
                <Link to="/">
                  <Button type="primary">{t(`ホーム画面へ`)}</Button>
                </Link>
                <br />
                <br />
                <Link to="/sign_out">
                  <Button type="default">{t(`ログアウト`)}</Button>
                </Link>
              </div>
            }
          />
        </CardContainer>
      ),
      effect(): void {
        //  empty
      },
    }
  }

  if (error.message.includes('Not Found') || error.message.includes('NotFound')) {
    return {
      effect(): void {
        // empty
      },
      component: (
        <Result
          status="404"
          title={t(`このリソースは存在しません`)}
          extra={
            <div className="text-center">
              <Link to="/">
                <Button type="primary">{t(`ホーム画面へ`)}</Button>
              </Link>
              <br />
              <br />
              <Link to="/sign_out">
                <Button type="default">{t(`ログイン画面へ`)}</Button>
              </Link>
            </div>
          }
        />
      ),
    }
  }

  if (error.message.includes('メールアドレス認証が完了していません')) {
    return {
      effect(): void {
        void navigate({ to: '/error', search: { type: 'waiting_verification' } })
      },
      component: <></>,
    }
  }

  return {
    component: (
      <Result
        icon={<></>}
        title={<span className="font-bold text-gray-600">{t(`予期せぬエラーが発生しました`)}</span>}
        extra={
          <>
            <span className="text-gray-500">
              {error.message} <br />
              {t(`リロード・再ログインしても解決しない場合は右下のチャットボットからお問い合わせください。`)}
            </span>
            <div className="mt-4 text-center">
              <Button
                shape="round"
                onClick={() => {
                  globalThis.location.reload()
                }}
              >
                <span className="text-xs font-bold">{t(`リロードする`)}</span>
              </Button>
            </div>
            <div className="mt-4 flex flex-col items-center justify-center">
              <Link to="/sign_out">
                <Button type="primary">{t('ログインページへ')}</Button>
              </Link>
            </div>
          </>
        }
      />
    ),
  }
}
